import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, UserPlus, Mail, Lock, User, Hash, IdCard } from 'lucide-react';
import axios from 'axios';

const LOGIN_BG = "https://images.unsplash.com/photo-1593113616828-6f22bca04804?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHx2b2x1bnRlZXIlMjBjb21tdW5pdHklMjBvdXRyZWFjaCUyMGZvb2QlMjBwYW50cnl8ZW58MHx8fHwxNzc2MjUyNzk3fDA&ixlib=rb-4.1.0&q=85";

export function LoginPage() {
  const [loginMode, setLoginMode] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pinVolId, setPinVolId] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error);
    }
  };

  const handlePinLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/auth/pin-login`, {
        volunteer_id: pinVolId, pin: pinCode
      }, { withCredentials: true });
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid Volunteer ID or PIN');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const { data } = await axios.get(`${API}/auth/google-login-url`);
      window.location.href = data.url;
    } catch {
      setError('Google login is not available right now');
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img src={LOGIN_BG} alt="Volunteers" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#3F6352]/80 to-[#D3735A]/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <img src="/svdp-logo.png" alt="SVdP Chicago" className="w-20 h-20 mb-6 rounded-full shadow-lg" />
          <h1 className="font-['Manrope'] text-4xl font-bold leading-tight mb-4">Community Service<br />Tracker</h1>
          <p className="text-lg opacity-90 font-light max-w-md">Powered by SVdP &bull; Mary, Mother of God Outreach Ministries</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAF8F5]">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-10">
            <h2 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] mb-2">Welcome back</h2>
            <p className="text-[#5C5855]">Sign in to track your volunteer hours</p>
          </div>

          <div className="flex gap-1 mb-6 bg-[#F2EFEB] p-1 rounded-lg">
            <button onClick={() => { setLoginMode('email'); setError(''); }}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-colors ${loginMode === 'email' ? 'bg-white text-[#2D2B2A] shadow-sm' : 'text-[#5C5855] hover:text-[#2D2B2A]'}`}
              data-testid="login-tab-email">
              <Mail className="h-4 w-4 inline mr-1" /> Email
            </button>
            <button onClick={() => { setLoginMode('pin'); setError(''); }}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-colors ${loginMode === 'pin' ? 'bg-white text-[#2D2B2A] shadow-sm' : 'text-[#5C5855] hover:text-[#2D2B2A]'}`}
              data-testid="login-tab-pin">
              <IdCard className="h-4 w-4 inline mr-1" /> Volunteer ID
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#B74134]/10 border border-[#B74134]/20 rounded-lg text-sm text-[#B74134]" data-testid="login-error">{error}</div>
          )}

          {loginMode === 'pin' ? (
            <form onSubmit={handlePinLogin} className="space-y-5">
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Volunteer ID</Label>
                <div className="relative">
                  <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                  <Input data-testid="pin-volunteer-id-input" type="text" value={pinVolId}
                    onChange={(e) => setPinVolId(e.target.value.toUpperCase())} placeholder="VOL-XXXXXX" required
                    className="pl-10 bg-white border-[#D1C9C0] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#D3735A]/50 font-mono" />
                </div>
              </div>
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">PIN</Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                  <Input data-testid="pin-code-input" type="password" value={pinCode}
                    onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="4-6 digit PIN"
                    required inputMode="numeric"
                    className="pl-10 bg-white border-[#D1C9C0] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#D3735A]/50 tracking-[0.2em]" />
                </div>
              </div>
              <Button data-testid="pin-login-submit" type="submit" disabled={submitting}
                className="w-full bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-6 py-3 font-semibold h-12">
                {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <><LogIn className="h-4 w-4 mr-2" /> Sign In with PIN</>}
              </Button>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                    <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com" required
                      className="pl-10 bg-white border-[#D1C9C0] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#D3735A]/50 focus:border-[#D3735A] text-[#2D2B2A] placeholder-[#8A847F]" />
                  </div>
                </div>
                <div>
                  <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                    <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password" required
                      className="pl-10 bg-white border-[#D1C9C0] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#D3735A]/50 focus:border-[#D3735A] text-[#2D2B2A] placeholder-[#8A847F]" />
                  </div>
                </div>
                <Button data-testid="login-submit-button" type="submit" disabled={submitting}
                  className="w-full bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-6 py-3 font-semibold transition-colors duration-200 shadow-sm h-12">
                  {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <><LogIn className="h-4 w-4 mr-2" /> Sign In</>}
                </Button>
              </form>
              <div className="flex justify-end mt-2">
                <Link to="/forgot-password" className="text-sm text-[#D3735A] hover:text-[#BD6047] font-medium" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <div className="my-6 flex items-center gap-4">
                <div className="flex-1 h-px bg-[#E8E3DD]" />
                <span className="text-xs text-[#8A847F] uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-[#E8E3DD]" />
              </div>
              <Button data-testid="google-login-button" type="button" onClick={handleGoogleLogin} variant="outline"
                className="w-full border-[#D1C9C0] bg-white hover:bg-[#F2EFEB] text-[#2D2B2A] rounded-lg px-6 py-3 font-semibold h-12">
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          <div className="mt-4 text-center">
            <Link to="/kiosk" className="text-sm text-[#3F6352] hover:text-[#2C4A3C] font-medium" data-testid="kiosk-link">Use Kiosk Mode (Check-in/Check-out)</Link>
          </div>

          <p className="mt-6 text-center text-sm text-[#5C5855]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#D3735A] hover:text-[#BD6047] font-semibold" data-testid="register-link">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await register(email, password, name);
    setSubmitting(false);
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const { data } = await axios.get(`${API}/auth/google-login-url`);
      window.location.href = data.url;
    } catch {
      setError('Google login is not available right now');
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img src={LOGIN_BG} alt="Volunteers" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#3F6352]/80 to-[#D3735A]/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <img src="/svdp-logo.png" alt="SVdP Chicago" className="w-20 h-20 mb-6 rounded-full shadow-lg" />
          <h1 className="font-['Manrope'] text-4xl font-bold leading-tight mb-4">Join Our<br />Community</h1>
          <p className="text-lg opacity-90 font-light max-w-md">Powered by SVdP &bull; Mary, Mother of God Outreach Ministries</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAF8F5]">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-10">
            <h2 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] mb-2">Create account</h2>
            <p className="text-[#5C5855]">Register as a volunteer</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#B74134]/10 border border-[#B74134]/20 rounded-lg text-sm text-[#B74134]" data-testid="register-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                <Input data-testid="register-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name" required className="pl-10 bg-white border-[#D1C9C0] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#D3735A]/50 focus:border-[#D3735A]" />
              </div>
            </div>
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                <Input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" required className="pl-10 bg-white border-[#D1C9C0] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#D3735A]/50 focus:border-[#D3735A]" />
              </div>
            </div>
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                <Input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password" required minLength={6} className="pl-10 bg-white border-[#D1C9C0] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#D3735A]/50 focus:border-[#D3735A]" />
              </div>
            </div>
            <Button data-testid="register-submit-button" type="submit" disabled={submitting}
              className="w-full bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-6 py-3 font-semibold h-12">
              {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><UserPlus className="h-4 w-4 mr-2" /> Create Account</>}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E8E3DD]" />
            <span className="text-xs text-[#8A847F] uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-[#E8E3DD]" />
          </div>

          <Button data-testid="google-register-button" type="button" onClick={handleGoogleLogin} variant="outline"
            className="w-full border-[#D1C9C0] bg-white hover:bg-[#F2EFEB] text-[#2D2B2A] rounded-lg px-6 py-3 font-semibold h-12">
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="mt-8 text-center text-sm text-[#5C5855]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D3735A] hover:text-[#BD6047] font-semibold" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
